import { useLazyQuery } from "@apollo/client";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Flex, Heading, Button, Box, Text } from "rebass/styled-components";
import { MotivationalQuote } from "../../../../components/MotivationalQuote";
import ScrollMenu from "../../../../components/ScrollMenu";
import { AutoResizeTextarea } from "../../../../components/TextArea";
import { GET_ENCOUNTER_SUMMARY, GET_HPIS, GET_SOAP_DRAFT } from "../../../../graphql/queries";
import { IGetEncounterSummaryOutputObject, IGetEncounterSummaryOutput, IGetEncounterSummaryInput, IGetSoapDraftInput, IGetSoapDraftOutput, IGetSoapDraftOutputObject } from "../../../../graphql/types/queries/appointment";
import { IGetHpisOutput, IGetHpisInput } from "../../../../graphql/types/queries/charts";
import theme from "../../../../styles/theme";
import { sleep } from "../../../../utils/sleep";
import { AiIcon } from "../../HpisWrapper";
import AiFormattedContent from "../HpiAiFormattedContent";
import { AppointmentSummary, SoapDraft } from "./AppointmentSummary";
import styled from "styled-components";
import ResizableLayout from "../../../../components/ResizableLayout";
import WherebyRoom from "../../../../components/WherebyRoom";

const LoadingText = styled(Text)`
  margin-top: 1rem;
  color: #4a5a77;
  font-size: 0.9rem;
`;

const Ctnrs = styled(Flex)`
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #6c89b7;
    border-radius: 5px;
  }
  &::-webkit-scrollbar {
    width: 7px;
    border: 1px solid transparent;
  }
`

export const CopyIcon = () => {
  return (<svg width="16" height="20" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 10C4 7.172 4 5.757 4.879 4.879C5.757 4 7.172 4 10 4H13C15.828 4 17.243 4 18.121 4.879C19 5.757 19 7.172 19 10V15C19 17.828 19 19.243 18.121 20.121C17.243 21 15.828 21 13 21H10C7.172 21 5.757 21 4.879 20.121C4 19.243 4 17.828 4 15V10Z" stroke="#344F7A" stroke-width="1.5" />
    <path opacity="0.5" d="M4 18C3.20435 18 2.44129 17.6839 1.87868 17.1213C1.31607 16.5587 1 15.7956 1 15V9C1 5.229 1 3.343 2.172 2.172C3.344 1.001 5.229 1 9 1H13C13.7956 1 14.5587 1.31607 15.1213 1.87868C15.6839 2.44129 16 3.20435 16 4" stroke="#344F7A" stroke-width="1.5" />
  </svg>
  )
}

const Spinner2 = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #2a3d66;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const Spinner = ({ size = 20, color = 'white', strokeWidth = 4 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 50 50"
    style={{ display: 'block', marginLeft: '16px' }}
  >
    <circle
      cx="25"
      cy="25"
      r="20"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeDasharray="100"
      strokeDashoffset="60"
    >
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="0 25 25"
        to="360 25 25"
        dur="1s"
        repeatCount="indefinite"
      />
    </circle>
  </svg>
);

export const safariExpression = /chrome|chromium|crios/i;

export const ViewHpi = ({ athenaId, selectedHpiTarget, setSelectedHpiTarget, onLoadingChange }: { athenaId: string, selectedHpiTarget: 'hypo' | 'meno' | 'weightlossInitial' | 'weightlossReturning' | undefined, setSelectedHpiTarget: (val: 'hypo' | 'meno' | 'weightlossInitial' | 'weightlossReturning' | undefined) => void, onLoadingChange: (loading: boolean) => void; }) => {
  const [isViewFullEncounterSummary, setIsViewFullEncounterSummary] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null);
  const [summary, setSummary] = useState<string>()
  const [hpi, setHpi] = useState<string>()
  const [summaryLastThyroid, setSummaryLastThyroid] = useState<IGetEncounterSummaryOutputObject | undefined>()
  const [summaryLastMeno, setSummaryLastMeno] = useState<IGetEncounterSummaryOutputObject | undefined>()
  const [summaryLastWL, setSummaryLastWL] = useState<IGetEncounterSummaryOutputObject | undefined>()
  const [soapDraft, setSoapDraft] = useState<IGetSoapDraftOutputObject | undefined>()
  const [triggerResize, setTriggerResize] =useState<number | undefined>()

  const [getSoapDraft, { loading: loadingSoapDraft }] = useLazyQuery<IGetSoapDraftOutput, IGetSoapDraftInput>(GET_SOAP_DRAFT, {

    onCompleted: (data) => {
      if (data && data.getHpiSoapDraft) {
        setSoapDraft(data.getHpiSoapDraft)
      }
    }
  })

  const [getLastAppointmentSummaryThyroid, { loading: loadingLastSummaryThyroid }] = useLazyQuery<IGetEncounterSummaryOutput, IGetEncounterSummaryInput>(GET_ENCOUNTER_SUMMARY, {
    variables: {
      patientId: athenaId,
      target: 'hypo'
    },
    onCompleted: (data) => {
      if (data && data.providersFullEncounterSummary) {
        setSummaryLastThyroid(data.providersFullEncounterSummary)
      }
    }
  })

  const [getLastAppointmentSummaryMeno, { loading: loadingLastSummaryMeno }] = useLazyQuery<IGetEncounterSummaryOutput, IGetEncounterSummaryInput>(GET_ENCOUNTER_SUMMARY, {
    variables: {
      patientId: athenaId,
      target: 'meno'
    },
    onCompleted: (data) => {
      if (data && data.providersFullEncounterSummary) {
        setSummaryLastMeno(data.providersFullEncounterSummary)
      }
    }
  })

  const [getLastAppointmentSummaryWL, { loading: loadingLastSummaryWL }] = useLazyQuery<IGetEncounterSummaryOutput, IGetEncounterSummaryInput>(GET_ENCOUNTER_SUMMARY, {
    variables: {
      patientId: athenaId,
      target: 'weightloss'
    },
    onCompleted: (data) => {
      if (data && data.providersFullEncounterSummary) {
        setSummaryLastWL(data.providersFullEncounterSummary)
      }
    }
  })

  const [loadHpis, { data, loading, error, refetch }] = useLazyQuery<IGetHpisOutput, IGetHpisInput>(GET_HPIS, {
    // fetchPolicy: 'network-only',
    onCompleted: (d) => {
      if (!d || !d.getHPIs || !d.getHPIs.copyable) {
        toast.error("No HPI found", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 5000
        })


        return
      }
      if (d?.getHPIs?.aiResult && d.getHPIs.aiResult.summary) {
        const sm = d.getHPIs.aiResult.summary
          .replace('Symptoms Paragraph', 'Symptoms')
          .replace('Lifestyle Paragraph', 'Lifestyle')
          .replace('Lab Trends Paragraph', 'Lab Trends')
        console.log({
          summary: d.getHPIs.aiResult.summary,
          sm
        })
        setSummary(sm)
      }
      if (d?.getHPIs.copyable) {
        setHpi(d.getHPIs.copyable.replace(/\n{3,}/g, '\n'))
      }
    }
  })

  useEffect(() => {
    if (!selectedHpiTarget) return
    loadHpis({
      variables: {
        athenaId,
        hpiTarget: selectedHpiTarget
      }
    })
  }, [selectedHpiTarget])

  useEffect(() => {
    onLoadingChange(loading);
  }, [loading, onLoadingChange]);

  const handleBeforeResize = () => {
    const el = containerRef.current as any;
    if (el) {
      el._scrollTopBefore = el.scrollTop;
    }
  };

  const handleAfterResize = () => {
    const el = containerRef.current as any;
    if (el && typeof el._scrollTopBefore === 'number') {
      el.scrollTop = el._scrollTopBefore;
    }
  };

  const scrollToBottomHpi = () => {
    const container = containerRef.current;
    if (container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
      // container.scrollTop = container.scrollHeight;
    }
  };

  if (!selectedHpiTarget) {
    return (
      <Flex flexDirection={'column'} style={{ padding: '24px' }} alignItems={'center'} justifyContent={'center'} flex={1}>
        <Heading textAlign={'center'} mb={4}>Select HPI to generate:</Heading>
        <Flex flexDirection={'column'} style={{ padding: '24px', }}>
          <Button variant={'primary'} onClick={() => setSelectedHpiTarget('hypo')}>Hypo HPI</Button>
          <Button variant={'pink'} mt={4} onClick={() => setSelectedHpiTarget('meno')}>Menopause HPI</Button>
          <Button variant={'pink'} mt={4} onClick={() => setSelectedHpiTarget('weightlossInitial')}>Weight Loss HPI: Initial</Button>
          <Button variant={'pink'} mt={4} onClick={() => {
            setSelectedHpiTarget('weightlossReturning')
          }}>Weight Loss HPI: Returning</Button>
        </Flex>
      </Flex>
    )
  }

  if (loading) {
    return (
      <Flex flexDirection={'column'} style={{ padding: '24px' }} alignItems={'center'} justifyContent={'center'} flex={1}>
        <Flex alignItems="center" justifyContent="center">
          <MotivationalQuote />
        </Flex>
        <Flex flexDirection={'column'} alignItems="center" justifyContent="center">
          <Spinner2 />
          <LoadingText style={{ textAlign: 'center' }}>Thanks for your patience — we're carefully generating your patient's HPI.<br />(The process can take more than 20 seconds)</LoadingText>
        </Flex>
      </Flex>
    )
  }

  if (error) {
    return (
      <Flex flexDirection={'column'} style={{ padding: '24px' }} alignItems={'center'} justifyContent={'center'} flex={1}>
        <Heading textAlign={'center'} mb={4}>HPI</Heading>

        <Text textAlign={'center'}>An error occured</Text>
        <Button variant={'primary'} onClick={() => refetch()}>Retry</Button>
      </Flex>
    )
  }

  return (
    <Flex flexDirection={'row'} flex={1} overflow={'hidden'}>

      <ResizableLayout triggerResize={setTriggerResize}>
        <Ctnrs flexDirection={'column'} overflow={'hidden'} style={{ padding: '24px 24px 24px 12px', overflow: 'auto', flex:1, color: '#344f7a', marginRight: '12px' }}>
          <AiFormattedContent
            talkingPoints={data?.getHPIs?.aiResult?.talkingPoints || ''}
            talkingPointsReasoning={data?.getHPIs.aiResult?.talkingPointsReasoning}
            recommendations={data?.getHPIs?.aiResult?.recommendations || ''}
            recommendationsReasoning={data?.getHPIs.aiResult?.recommendationsReasoning}
          />
        </Ctnrs>

        <Ctnrs flexDirection={'column'} flex={1}
          ref={containerRef} 
          style={{ padding: '24px 12px 24px 24px', background: 'white', overflow: 'auto', maxHeight: '100%', minHeight: '100%', boxShadow: '-3px 15px 10px 0px #344f7a73', borderRadius: '10px', paddingBottom: '250px' }}>

          {(!summaryLastThyroid || !summaryLastMeno || !summaryLastWL || !soapDraft) && (
            <ScrollMenu entries={[{
              text: 'Summary from last thyroid visit',
              onClick: async () => {
                getLastAppointmentSummaryThyroid()
                // await sleep(200)
                scrollToBottomHpi()
              },
              isDisabled: !!summaryLastThyroid || !!loadingLastSummaryThyroid
            }, {
              text: 'Summary from last menopause visit',
              onClick: async () => {
                getLastAppointmentSummaryMeno()
                await sleep(200)
                scrollToBottomHpi()
              },
              isDisabled: !!summaryLastMeno || !!loadingLastSummaryMeno
            }, {
              text: 'Summary from last weight loss visit',
              onClick: async () => {
                getLastAppointmentSummaryWL()
                await sleep(200)
                scrollToBottomHpi()
              },
              isDisabled: !!summaryLastWL || !!loadingLastSummaryWL
            }, {
              text: 'Generate SOAP draft',
              onClick: async () => {
                getSoapDraft({
                  variables: {
                    summary: summary || '',
                    hpi: hpi || ''
                  },
                })
                await sleep(200)
                scrollToBottomHpi()
              },
              isDisabled: !!soapDraft || !!loadingSoapDraft
            },
            ]} />
          )}
          <Flex flex={1} flexDirection={'column'}>


<WherebyRoom roomUrl={'https://paloma.whereby.com/florianhre7r6?roomKey=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJtZWV0aW5nSWQiOiI1NTEyNTgxMiIsInJvb21SZWZlcmVuY2UiOnsicm9vbU5hbWUiOiIvZmxvcmlhbmhyZTdyNiIsIm9yZ2FuaXphdGlvbklkIjoiMTU5NTg3In0sImlzcyI6Imh0dHBzOi8vYWNjb3VudHMuc3J2LndoZXJlYnkuY29tIiwiaWF0IjoxNzU0Njg4OTI0LCJyb29tS2V5VHlwZSI6Im1lZXRpbmdIb3N0In0.3LOYNMS3OCcBEjpWuOHShCDpgsfZfYC_CqG2KkCwLE8'} />





            {!!data?.getHPIs?.aiResult?.summary && (
              <Flex mb={4} style={{ position: 'relative' }}>
                <Box style={{ width: '2px', background: '#B8D6F5', height: '100%' }} mr={3}></Box>
                <Flex flexDirection={'column'} flex={1}>
                  <Flex justifyContent={'start'} alignItems={'center'} style={{ gap: '16px' }} mb={2}>
                    <Text fontSize={'16px'} fontWeight={600}>Patient Summary</Text>
                    <AiIcon />
                    <Text fontWeight={300} fontSize={'12px'}>(editable)</Text>
                    {safariExpression.test(navigator.userAgent) && (
                      <Flex ml={3} justifyContent={'center'} style={{ cursor: 'pointer' }} onClick={() => {
                        navigator.clipboard.writeText(summary || '')
                        toast.success("Patient Summary copied to clipboard", {
                          position: toast.POSITION.TOP_RIGHT,
                          autoClose: 5000
                        });
                      }}>
                        <CopyIcon />
                        <Text ml={2} fontWeight={600} fontSize={'12px'} style={{ color: '#0095F9', textDecoration: 'underline' }}>Copy</Text>
                      </Flex>
                    )}
                  </Flex>
                  <AutoResizeTextarea
                    style={{ backgroundColor: 'white', border: 0, color: theme.color.primary, padding: 0, minHeight: '50px', fontSize: '14px' }}
                    onChange={(e: any) => {
                      handleBeforeResize();
                      setSummary(e.target.value)
                    }}
                    onHeightChange={handleAfterResize}
                    resizeTrigger={triggerResize}
                    value={summary || ''} />
                </Flex>
              </Flex>
            )}
            {!!data?.getHPIs.copyable && (
              <Flex mb={4}>
                <Box style={{ width: '2px', background: '#FFABA0', height: '100%' }} mr={3}></Box>
                <Flex flexDirection={'column'} flex={1}>
                  <Flex justifyContent={'start'} alignItems={'center'} style={{ gap: '16px' }} mb={2}>
                    <Text fontSize={'16px'} fontWeight={600}>History of Present Illness</Text>
                    <Text fontWeight={300} fontSize={'12px'}>(editable)</Text>
                    {safariExpression.test(navigator.userAgent) && (
                      <Flex ml={3} justifyContent={'center'} style={{ cursor: 'pointer' }} onClick={() => {
                        navigator.clipboard.writeText(hpi || '')
                        toast.success("HPI copied to clipboard", {
                          position: toast.POSITION.TOP_RIGHT,
                          autoClose: 5000
                        });
                      }}>
                        <CopyIcon />
                        <Text ml={2} fontWeight={600} fontSize={'12px'} style={{ color: '#0095F9', textDecoration: 'underline' }}>Copy</Text>
                      </Flex>
                    )}
                  </Flex>
                  <AutoResizeTextarea
                    style={{ backgroundColor: 'white', border: 0, color: theme.color.primary, padding: 0, minHeight: '50px', fontSize: '14px' }}
                    onChange={(e: any) => {
                      handleBeforeResize();
                      setHpi(e.target.value)
                    }}
                    onHeightChange={handleAfterResize}
                    resizeTrigger={triggerResize}
                    value={hpi || ''} />
                </Flex>
              </Flex>
            )}
            {(!!summaryLastThyroid || !!loadingLastSummaryThyroid) && (
              <Flex mb={4}>
                <Box style={{ width: '2px', background: '#40df38', height: '100%' }} mr={3}></Box>
                <Flex flexDirection={'column'} flex={1}>
                  <Text fontSize={'16px'} fontWeight={600} mb={2}>Summary from last thyroid visit</Text>
                  {!!loadingLastSummaryThyroid ? (
                    <Flex flex={1}>
                      <Spinner color={theme.color.primary} />
                      <Text fontWeight={300} fontSize={'13px'} ml={3}>Loading</Text>
                    </Flex>
                  ) :
                    <AppointmentSummary data={summaryLastThyroid!} />
                  }
                </Flex>
              </Flex>
            )}
            {(!!summaryLastMeno || !!loadingLastSummaryMeno) && (
              <Flex mb={4}>
                <Box style={{ width: '2px', background: '#dfd638', height: '100%' }} mr={3}></Box>
                <Flex flexDirection={'column'} flex={1}>
                  <Text fontSize={'16px'} fontWeight={600} mb={2}>Summary from last menopause visit</Text>
                  {!!loadingLastSummaryMeno ? (
                    <Flex>
                      <Spinner />
                      <Text fontWeight={300} fontSize={'13px'} ml={3}>Loading</Text>
                    </Flex>
                  ) :
                    <AppointmentSummary data={summaryLastMeno!} />
                  }
                </Flex>
              </Flex>
            )}
            {(!!summaryLastWL || !!loadingLastSummaryWL) && (
              <Flex mb={4}>
                <Box style={{ width: '2px', background: '#df8f38', height: '100%' }} mr={3}></Box>
                <Flex flexDirection={'column'} flex={1}>
                  <Text fontSize={'16px'} fontWeight={600} mb={2}>Summary from last weight loss visit</Text>
                  {!!loadingLastSummaryWL ? (
                    <Flex>
                      <Spinner />
                      <Text fontWeight={300} fontSize={'13px'} ml={3}>Loading</Text>
                    </Flex>
                  ) :
                    <AppointmentSummary data={summaryLastWL!} />
                  }
                </Flex>
              </Flex>
            )}
            {(!!soapDraft || !!loadingSoapDraft) && (
              <Flex mb={4}>
                <Box style={{ width: '2px', background: '#df8f38', height: '100%' }} mr={3}></Box>
                <Flex flexDirection={'column'} flex={1}>
                  <Text fontSize={'16px'} fontWeight={600} mb={2}>SOAP draft</Text>
                  {!!loadingSoapDraft ? (
                    <Flex>
                      <Spinner />
                      <Text fontWeight={300} fontSize={'13px'} ml={3}>Loading</Text>
                    </Flex>
                  ) :
                    <SoapDraft data={soapDraft!} handleAfterResize={handleAfterResize} handleBeforeResize={handleBeforeResize}
                      setData={setSoapDraft}
                    />
                  }
                </Flex>
              </Flex>
            )}
          </Flex>
        </Ctnrs>
      </ResizableLayout>
    </Flex>
  )
}